import React, { useState } from 'react'

import toast from 'react-hot-toast'
import { PiFileAudio } from 'react-icons/pi'

import FileComponent from './FileComponent'
import FilesToUploadList from './FilesToUploadList'
import {
  FilesList,
  FormBtnsContainer,
  InputFileForm,
  UploadedItems,
} from './style'
import UploadedFilesList from './UploadedFilesList'
import { Button, DeleteLogModal } from '../..'
import { useStorage } from '../../../context'
import { AudioRef, FileItem } from '../../../types'
import { SectionSubtitle } from '../../texts'

interface InputFileProps {
  userId?: string
  logUID?: string
  updateLogData: (files: AudioRef[] | undefined) => void
  readOnly: boolean
  defaultValue?: AudioRef[] // 👈 siempre array
}

const InputFile = ({
  userId,
  logUID,
  updateLogData,
  readOnly,
  defaultValue = [],
}: InputFileProps) => {
  const { uploadAudio, deleteAudio, loadingStorage } = useStorage()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [files, setFiles] = useState<FileItem[]>([])
  const [uploadingBatch, setUploadingBatch] = useState(false)

  const mergeAudioRefs = (current: AudioRef[], next: AudioRef[]) => {
    const byKey = new Map<string, AudioRef>()

    current.forEach((audio) => {
      byKey.set(audio.path ?? audio.url, audio)
    })
    next.forEach((audio) => {
      byKey.set(audio.path ?? audio.url, audio)
    })

    return Array.from(byKey.values())
  }

  const uploadSingleFile = async (
    fileItem: {
      id: string
      file: File
      progress: number
    },
    options?: { syncLogData?: boolean },
  ): Promise<AudioRef | undefined> => {
    if (!userId || !logUID) return undefined

    const shouldSyncLogData = options?.syncLogData ?? true

    const updateProgress = (id: string, p: number) => {
      setFiles((prev) =>
        prev.map((it) => (it.id === id ? { ...it, progress: p } : it)),
      )
    }

    try {
      const res = await uploadAudio(
        fileItem.file,
        userId,
        logUID,
        fileItem.id,
        (percent: number) => updateProgress(fileItem.id, percent),
      )

      updateProgress(fileItem.id, 100)

      setFiles((prev) =>
        prev.map((it) =>
          it.id === fileItem.id
            ? {
                ...it,
                uploaded: true,
                url: res.url,
                path: res.fullPath,
                progress: 100,
              }
            : it,
        ),
      )

      const newAudio: AudioRef = {
        title: fileItem.file.name,
        url: res.url,
        path: res.fullPath,
      }

      if (shouldSyncLogData) {
        updateLogData(mergeAudioRefs(defaultValue, [newAudio]))
      }

      toast.success(`Archivo ${fileItem.file.name} cargado`)
      return newAudio
    } catch (error) {
      console.error('Error al subir el archivo:', error)
      toast.error(`Error al subir ${fileItem.file.name}`)
      return undefined
    }
  }

  // Subir todos y sincronizar la bitácora una sola vez para no perder archivos
  // por actualizaciones concurrentes del estado padre.
  const handleUploadAll = async () => {
    if (!userId || !logUID || files.length === 0) return
    setUploadingBatch(true)
    try {
      const pending = files.filter((f) => !f.uploaded) // 👈 solo los no subidos
      if (pending.length === 0) return

      const uploadedAudios: AudioRef[] = []

      for (const file of pending) {
        const uploadedAudio = await uploadSingleFile(file, {
          syncLogData: false,
        })
        if (uploadedAudio) {
          uploadedAudios.push(uploadedAudio)
        }
      }

      if (uploadedAudios.length > 0) {
        updateLogData(mergeAudioRefs(defaultValue, uploadedAudios))
      }
    } catch (error) {
      console.error('Error en subida paralela:', error)
    } finally {
      setUploadingBatch(false)
    }
  }

  const handleDeleteAll = async () => {
    if (!userId || !logUID) return
    setShowConfirmation(false)
    try {
      if (files.length > 0) {
        await Promise.all(
          files
            .filter((f) => f.url)
            .map((f) =>
              deleteAudio(userId, logUID, f.path ?? (f.url as string)),
            ),
        )

        setFiles([])
        updateLogData(undefined)
      } else if (defaultValue.length > 0) {
        await Promise.all(
          defaultValue.map((audio) =>
            deleteAudio(userId, logUID, audio.path ?? audio.url),
          ),
        )
        updateLogData(undefined)
      }

      toast.success('Se han eliminado todos los archivos')
    } catch (err) {
      console.error('Error al eliminar todos los archivos:', err)
      toast.error('Error al eliminar algunos archivos')
    }
  }

  return (
    <div>
      {showConfirmation && (
        <DeleteLogModal
          handleClose={() => setShowConfirmation(false)}
          handleDelete={handleDeleteAll}
          message="¿Estas seguro@ que quieres eliminar todos los archivos?"
          hasButton
        />
      )}
      {readOnly ? (
        <>
          {defaultValue.length > 0 ? (
            <FilesList style={{ flexWrap: 'wrap' }}>
              {defaultValue.map((file) => (
                <UploadedItems
                  key={file.url}
                  style={{
                    maxWidth: 'calc(50% - 8px)',
                    height: '-webkit-fill-available',
                  }}
                >
                  <span>
                    <PiFileAudio className="icon" aria-hidden="true" />
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={file.title}
                    >
                      {file.title.slice(0, file.title.lastIndexOf('.'))}
                    </a>
                  </span>
                </UploadedItems>
              ))}
            </FilesList>
          ) : (
            <p>No se ha cargado ningún audio</p>
          )}
        </>
      ) : (
        <InputFileForm>
          <FileComponent
            files={files}
            setFiles={setFiles}
            defaultValue={defaultValue}
          />
          <UploadedFilesList
            userId={userId}
            logUID={logUID}
            updateLogData={updateLogData}
            defaultValue={defaultValue}
            files={files}
          />
          <FilesToUploadList
            userId={userId}
            logUID={logUID}
            defaultValue={defaultValue}
            files={files}
            setFiles={setFiles}
            updateLogData={updateLogData}
            uploadSingleFile={uploadSingleFile}
          />

          <FormBtnsContainer>
            <Button
              onClick={handleUploadAll}
              disabled={
                uploadingBatch ||
                loadingStorage ||
                files.length === 0 ||
                defaultValue.length > files.length
              }
              label="Subir todo"
              className="light-blue"
            />
            <Button
              onClick={() => setShowConfirmation(true)}
              disabled={
                loadingStorage ||
                (defaultValue.length === 0 && files.length === 0)
              }
              style={{ marginLeft: 8 }}
              label="Eliminar todo"
              className="delete"
            />
          </FormBtnsContainer>
          <SectionSubtitle
            fontSize={11}
            fontWeight={400}
            children="Los audios seleccionados se asociarán a la bitácora cuando pulses GUARDAR."
            textAlign="left"
          />
          <SectionSubtitle
            fontSize={11}
            fontWeight={400}
            children="Si cancelas una bitácora nueva, sus audios cargados también se eliminarán."
            textAlign="left"
          />
        </InputFileForm>
      )}
    </div>
  )
}

export default InputFile
